import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';

import { SampleManagerService } from '../../services/sample-manager.service';
import { CisProLookupDto } from '@models/cis-pro-lookup-dto.interface';

/**
 * Feature B — barcode lookup panel.
 *
 * Emits `lookupResult` when a live result is returned.
 * The parent (edit-material-dialog) uses it to auto-merge field values.
 *
 * `readonly`: when true the barcode input is disabled (material is saved).
 * `scannerEvents$`: optional observable — when emitted, triggers a lookup automatically
 *   (used for hardware barcode-scanner integration).
 */
@Component({
  selector: 'app-sample-manager-lookup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './sample-manager-lookup.component.html',
  styleUrl: './sample-manager-lookup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SampleManagerLookupComponent implements OnInit, OnDestroy {
  private readonly sampleManagerService = inject(SampleManagerService);
  private readonly destroy$ = new Subject<void>();

  /** When true the barcode input is read-only (material already saved). */
  readonly isReadonly = input<boolean>(false);

  /** Optionally wire a barcode scanner stream from the parent. */
  readonly scannerEvents$ = input<Subject<string> | null>(null);

  /** Pre-fill the barcode input (e.g. when editing an existing material). */
  readonly initialBarcode = input<string | null>(null);

  /** Emitted on a successful lookup so the parent can merge the result. */
  @Output() lookupResult = new EventEmitter<CisProLookupDto>();

  /** Emitted with an error message when the lookup fails or is not found. */
  @Output() lookupError = new EventEmitter<string>();

  protected barcodeControl = new FormControl('');
  protected isLoading = signal(false);
  protected result = signal<CisProLookupDto | null>(null);
  protected notFoundMessage = signal<string | null>(null);
  protected errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    if (this.initialBarcode()) {
      this.barcodeControl.setValue(this.initialBarcode());
    }

    if (this.isReadonly()) {
      this.barcodeControl.disable();
    }

    // Subscribe to barcode scanner stream if provided.
    const scanner$ = this.scannerEvents$();
    if (scanner$) {
      scanner$.pipe(takeUntil(this.destroy$)).subscribe(barcode => {
        if (!this.isReadonly()) {
          this.barcodeControl.setValue(barcode);
          this.runLookup(barcode);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onSearchClick(): void {
    const barcode = (this.barcodeControl.value ?? '').trim();
    if (!barcode) return;
    this.runLookup(barcode);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearchClick();
    }
  }

  private runLookup(barcode: string): void {
    const trimmed = barcode.trim();
    if (!trimmed) return;

    this.isLoading.set(true);
    this.notFoundMessage.set(null);
    this.errorMessage.set(null);

    this.sampleManagerService.getByBarcode(trimmed).subscribe({
      next: dto => {
        this.result.set(dto);
        this.lookupResult.emit(dto);
        this.isLoading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.result.set(null);
        this.isLoading.set(false);

        if (err.status === 404) {
          const msg = `Barcode [${trimmed}] wasn't found`;
          this.notFoundMessage.set(msg);
          this.lookupError.emit(msg);
        } else {
          const msg = err.error?.detail ?? err.message ?? 'SampleManager lookup failed.';
          this.errorMessage.set(msg);
          this.lookupError.emit(msg);
        }
      },
    });
  }
}
